import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/contexts/PermissionsContext";
import {
  createAdminUser,
  formatAdminApiError,
  listAdminUsers,
  unlockAdminUser,
  updateAdminUser,
} from "@/lib/adminUsersApi";
import { listAdminGroups } from "@/lib/adminGroupsApi";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, RefreshCw, Users } from "lucide-react";
import { toast } from "sonner";

import {
  formatGroupLabels,
  GROUP_DISPLAY_NAMES,
  normalizeUserGroupSlugs,
} from "@/lib/rbacGroups";

function groupLabel(slug, rbacGroups) {
  const normalized = normalizeUserGroupSlugs([slug])[0] || slug;
  const match = rbacGroups.find((g) => g.slug === normalized);
  return match?.display_name || GROUP_DISPLAY_NAMES[normalized] || normalized;
}

/**
 * User management — invite / assign RBAC roles (Cognito groups or Entra app roles).
 */
export default function SettingsUserManagement() {
  const { user: currentUser, idpMode } = useAuth();
  const entraMode = idpMode === "entra";
  const { hasPermission, groups: actorGroups } = usePermissions();
  const canCreate = hasPermission("settings.user-management", "create");
  const canUpdate = hasPermission("settings.user-management", "update");
  const isPlatformAdministrator = actorGroups.includes("administrator");
  const isUserAccessAdminOnly =
    actorGroups.includes("user-access-admin") && !isPlatformAdministrator;

  const isSelfAccount = (row) => {
    const ids = new Set(
      [currentUser?.sub, currentUser?.email, currentUser?.username].filter(Boolean),
    );
    return ids.has(row.username) || ids.has(row.email) || ids.has(row.sub);
  };

  const targetIsPlatformAdministrator = (row) =>
    normalizeUserGroupSlugs(row.groups).includes("administrator");

  const canManageAccount = (row) => {
    if (!canUpdate) return false;
    if (isSelfAccount(row)) return false;
    if (isUserAccessAdminOnly && targetIsPlatformAdministrator(row)) return false;
    return true;
  };

  const canAssignGroupSlug = (slug, row = null) => {
    if (slug !== "administrator") return true;
    if (isPlatformAdministrator) return true;
    if (isUserAccessAdminOnly) return false;
    if (row && targetIsPlatformAdministrator(row)) return false;
    return false;
  };

  const [users, setUsers] = useState([]);
  const [rbacGroups, setRbacGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingUsername, setSavingUsername] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteGroupSlugs, setInviteGroupSlugs] = useState(["ticket-operator"]);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [groupsEditorUser, setGroupsEditorUser] = useState(null);
  const [editGroupSlugs, setEditGroupSlugs] = useState([]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [usersData, groupsData] = await Promise.all([
        listAdminUsers(),
        listAdminGroups().catch(() => []),
      ]);
      setUsers(usersData.users || []);
      const catalog = Array.isArray(groupsData) ? groupsData : [];
      setRbacGroups(catalog);
      setInviteGroupSlugs((prev) => {
        if (catalog.some((g) => g.slug === prev[0])) return prev;
        return catalog[0]?.slug ? [catalog[0].slug] : [];
      });
    } catch (err) {
      const message = formatAdminApiError(err);
      setError(message);
      setUsers([]);
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleToggleEnabled = async (row) => {
    const nextEnabled = !row.enabled;
    if (!nextEnabled && isSelfAccount(row)) {
      toast.error("You cannot disable your own account.");
      return;
    }
    if (!nextEnabled && isUserAccessAdminOnly && targetIsPlatformAdministrator(row)) {
      toast.error("User & Access Admins cannot modify platform administrators.");
      return;
    }
    setSavingUsername(row.username);
    try {
      const updated = await updateAdminUser(row.username, { enabled: nextEnabled });
      setUsers((prev) =>
        prev.map((u) => (u.username === row.username ? updated : u)),
      );
      toast.success(
        nextEnabled
          ? entraMode
            ? "Halo access restored"
            : "User enabled"
          : entraMode
            ? "Halo access removed (Entra account unchanged)"
            : "User disabled",
      );
    } catch (err) {
      toast.error(formatAdminApiError(err));
    } finally {
      setSavingUsername(null);
    }
  };

  const handleUnlock = async (row) => {
    setSavingUsername(row.username);
    try {
      const updated = await unlockAdminUser(row.username);
      setUsers((prev) =>
        prev.map((u) => (u.username === row.username ? updated : u)),
      );
      toast.success("Sign-in lockout cleared");
    } catch (err) {
      toast.error(formatAdminApiError(err));
    } finally {
      setSavingUsername(null);
    }
  };

  const handleGroupsSave = async () => {
    if (!groupsEditorUser || !canUpdate) return;
    if (!editGroupSlugs.length) {
      toast.error("Select at least one access group");
      return;
    }
    const editorRow = users.find((u) => u.username === groupsEditorUser);
    if (
      isUserAccessAdminOnly &&
      editorRow &&
      targetIsPlatformAdministrator(editorRow) &&
      !editGroupSlugs.includes("administrator")
    ) {
      toast.error("User & Access Admins cannot modify platform administrators.");
      return;
    }
    if (isUserAccessAdminOnly && editGroupSlugs.includes("administrator")) {
      toast.error("User & Access Admins cannot assign the administrator group.");
      return;
    }
    setSavingUsername(groupsEditorUser);
    try {
      const updated = await updateAdminUser(groupsEditorUser, {
        group_slugs: editGroupSlugs,
      });
      setUsers((prev) =>
        prev.map((u) => (u.username === groupsEditorUser ? updated : u)),
      );
      setGroupsEditorUser(null);
      toast.success("Access groups updated");
    } catch (err) {
      toast.error(formatAdminApiError(err));
    } finally {
      setSavingUsername(null);
    }
  };

  const openGroupsEditor = (row) => {
    const slugs = normalizeUserGroupSlugs(row.groups);
    const visible = slugs.filter((s) => rbacGroups.some((g) => g.slug === s));
    setEditGroupSlugs(
      visible.length ? visible : rbacGroups[0]?.slug ? [rbacGroups[0].slug] : [],
    );
    setGroupsEditorUser(row.username);
  };

  const toggleInviteGroup = (slug) => {
    setInviteGroupSlugs([slug]);
  };

  const toggleEditGroup = (slug) => {
    setEditGroupSlugs([slug]);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) {
      toast.error("Email is required");
      return;
    }
    if (!inviteGroupSlugs.length) {
      toast.error("Select at least one access group");
      return;
    }
    if (isUserAccessAdminOnly && inviteGroupSlugs.includes("administrator")) {
      toast.error("User & Access Admins cannot assign the administrator group.");
      return;
    }
    setInviteSubmitting(true);
    try {
      const created = await createAdminUser({
        email,
        group_slugs: inviteGroupSlugs,
      });
      setUsers((prev) => {
        const exists = prev.some((u) => u.username === created.username);
        return exists
          ? prev.map((u) => (u.username === created.username ? created : u))
          : [created, ...prev];
      });
      toast.success(
        entraMode
          ? "Halo access granted. The user can sign in with Microsoft."
          : "Invitation sent. The user will receive a temporary password by email.",
      );
      setInviteOpen(false);
      setInviteEmail("");
      setInviteGroupSlugs(["ticket-operator"]);
    } catch (err) {
      toast.error(formatAdminApiError(err));
    } finally {
      setInviteSubmitting(false);
    }
  };

  return (
    <Card data-testid="settings-user-management">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-cp-indigo" />
            User management
          </CardTitle>
          <CardDescription>
            {entraMode
              ? "Grant Halo access to people who already exist in your Microsoft Entra tenant. Assign roles, or remove Halo access without disabling their Entra account."
              : "Invite users, assign access groups, and enable or disable accounts. Groups control permissions in Cognito and Postgres."}
          </CardDescription>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={loadUsers}
            disabled={loading}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button type="button" size="sm" disabled={!canCreate}>
                <Plus className="mr-2 h-4 w-4" />
                {entraMode ? "Add user" : "Invite user"}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <form onSubmit={handleInvite}>
                <DialogHeader>
                  <DialogTitle>{entraMode ? "Add existing user" : "Invite user"}</DialogTitle>
                  <DialogDescription>
                    {entraMode
                      ? "The person must already exist in your Entra tenant (your Entra admin adds them there). Halo assigns a Halo app role only — we do not invite guests into the directory."
                      : "Cognito sends an email with a temporary password. The user must set a new password on first sign-in."}
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <Label htmlFor="invite-email">Email</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      autoComplete="off"
                      value={inviteEmail}
                      onChange={(ev) => setInviteEmail(ev.target.value)}
                      placeholder="name@company.com"
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Access role (one)</Label>
                    <div className="rounded-md border p-3 space-y-2 max-h-48 overflow-y-auto">
                      {rbacGroups.map((g) => (
                        <label
                          key={g.id}
                          className="flex items-center gap-2 text-sm cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="invite-role"
                            className="h-4 w-4"
                            checked={inviteGroupSlugs[0] === g.slug}
                            disabled={!canAssignGroupSlug(g.slug)}
                            onChange={() => toggleInviteGroup(g.slug)}
                          />
                          <span>{g.display_name}</span>
                          <span className="text-xs text-muted-foreground font-mono">{g.slug}</span>
                          {g.description ? (
                            <span className="text-xs text-muted-foreground truncate max-w-[12rem]" title={g.description}>
                              — {g.description}
                            </span>
                          ) : null}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setInviteOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={inviteSubmitting}>
                    {inviteSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending…
                      </>
                    ) : entraMode ? (
                      "Grant access"
                    ) : (
                      "Send invite"
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading users…
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Access groups</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No users found.
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((row) => {
                    const busy = savingUsername === row.username;
                    const isSelf = isSelfAccount(row);
                    const manageAccount = canManageAccount(row);
                    return (
                      <TableRow key={row.username}>
                        <TableCell>
                          <div className="font-medium">{row.email || "—"}</div>
                          <div className="text-xs text-muted-foreground font-mono truncate max-w-[220px]">
                            {row.username}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {normalizeUserGroupSlugs(row.groups).map((slug) => (
                              <Badge key={slug} variant="secondary" className="text-xs">
                                {groupLabel(slug, rbacGroups)}
                              </Badge>
                            ))}
                          </div>
                          {canUpdate && manageAccount ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="link"
                              className="h-auto p-0 mt-1"
                              onClick={() => openGroupsEditor(row)}
                            >
                              Edit groups
                            </Button>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">{row.status || "—"}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant={row.enabled ? "secondary" : "destructive"}>
                            {row.enabled ? "Enabled" : "Disabled"}
                          </Badge>
                          {row.account_locked ? (
                            <Badge variant="destructive" className="ml-2">
                              Sign-in locked
                            </Badge>
                          ) : null}
                          {row.failed_login_attempts > 0 && !row.account_locked ? (
                            <span className="block text-xs text-muted-foreground mt-1">
                              {row.failed_login_attempts} failed attempt
                              {row.failed_login_attempts === 1 ? "" : "s"}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right space-x-2">
                          {row.account_locked && manageAccount ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={busy}
                              onClick={() => handleUnlock(row)}
                            >
                              {busy ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                "Unlock"
                              )}
                            </Button>
                          ) : null}
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={busy || !manageAccount}
                            onClick={() => handleToggleEnabled(row)}
                          >
                            {busy ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : row.enabled ? (
                              "Disable"
                            ) : (
                              "Enable"
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog
        open={Boolean(groupsEditorUser)}
        onOpenChange={(open) => !open && setGroupsEditorUser(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit access role</DialogTitle>
            <DialogDescription>
              Assign exactly one role. Cognito membership updates immediately. The user
              must sign out and sign in again to receive a new token with updated scopes
              (Check again cannot refresh after a role change).
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border p-3 space-y-2 max-h-60 overflow-y-auto">
            {rbacGroups.map((g) => {
              const editorRow = users.find((u) => u.username === groupsEditorUser);
              return (
              <label
                key={g.id}
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <input
                  type="radio"
                  name="edit-role"
                  className="h-4 w-4"
                  checked={editGroupSlugs[0] === g.slug}
                  disabled={!canAssignGroupSlug(g.slug, editorRow)}
                  onChange={() => toggleEditGroup(g.slug)}
                />
                <span>{g.display_name}</span>
                <span className="text-xs text-muted-foreground font-mono">{g.slug}</span>
              </label>
            );
            })}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setGroupsEditorUser(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleGroupsSave}
              disabled={savingUsername === groupsEditorUser || !editGroupSlugs.length}
            >
              Save groups
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
